"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type TransitionEvent,
} from "react";

import { useCopyLang } from "@/components/layout/use-site-ui-copy";
import { getProductCanvasCopy } from "./product-canvas-copy";

import Image from "next/image";

import {
  getAutoFlowIcon,
  type AutoFlowIconName,
} from "@/components/services/ai-automation/auto-flow-icons";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import {
  getProductCanvasBrandLogoSrc,
  type CcProductCanvasBrandIconId,
} from "@/lib/cc-product-canvas-brand-icons";
import { devSectionAttr } from "@/lib/dev/section-attr";

// The section is taller than the viewport at every breakpoint, so an
// intersection *ratio* can never be a reliable trigger — at 1440x600 it peaks at
// 0.46 and on a phone at 0.39, which left the whole section stuck at opacity 0.
// Trigger on the section's leading edge instead: a root shrunk by 25% of the
// viewport height fires once the section top passes 75% down the screen, which is
// where the eyebrow first becomes visible, and is independent of section height.
const APPEAR_THRESHOLD = 0;
const APPEAR_ROOT_MARGIN = "0px 0px -25% 0px";

const BRANCH_PATH =
  "M 211 182 L 248 182 C 255.18 182 261 187.82 261 195 L 261 243 C 261 250.18 255.18 256 248 256 L 13 256 C 5.82 256 0 250.18 0 243 L 0 13 C 0 5.82 5.82 0 13 0 L 48 0";

const MAIN_PATH =
  "M 0 0 L 677 0 C 684.18 0 690 5.82 690 13 L 690 79.813 C 690 86.993 684.18 92.813 677 92.813 L 245.994 92.813 C 238.814 92.813 232.994 98.633 232.994 105.813 L 232.994 185 C 232.994 192.18 238.814 198 245.994 198 L 438.478 198 L 438.478 190.688 L 563.678 190.688 L 563.678 198 L 655.191 198";

const HATCH_RULER_PATH =
  "M 1.56 0 L 13.994 10.07 L 12.435 11.995 L 0.001 1.926 Z M 1.56 6.327 L 13.995 16.397 L 12.435 18.322 L 0.001 8.253 Z M 1.559 12.655 L 13.994 22.725 L 12.434 24.65 L 0 14.58 Z M 1.559 18.982 L 13.994 29.052 L 12.434 30.977 L 0 20.908 Z M 1.559 25.309 L 13.994 35.379 L 12.434 37.304 L 0.001 27.235 Z M 1.56 31.636 L 13.994 41.706 L 12.434 43.631 L 0.001 33.562 Z M 1.559 38.055 L 13.994 48.125 L 12.434 50.05 L 0 39.981 Z M 1.559 44.475 L 13.994 54.543 L 12.434 56.469 L 0 46.4 Z M 1.559 50.893 L 13.994 60.963 L 12.434 62.888 L 0 52.819 Z M 1.559 57.312 L 13.994 67.382 L 12.434 69.307 L 0 59.238 Z M 1.559 63.731 L 13.994 73.801 L 12.434 75.726 L 0 65.657 Z M 1.559 70.15 L 13.994 80.22 L 12.434 82.145 L 0 72.075 Z M 1.559 76.569 L 13.994 86.639 L 12.434 88.564 L 0 78.494 Z M 1.559 82.988 L 13.994 93.058 L 12.434 94.983 L 0 84.913 Z";

const HASH_BAR_PATH =
  "M 56.561 14.72 L 40.21 0 L 38.553 1.84 L 54.903 16.561 Z M 64.021 14.72 L 47.672 0 L 46.014 1.84 L 62.364 16.561 Z M 71.482 14.72 L 55.133 0 L 53.475 1.84 L 69.825 16.561 Z M 78.943 14.72 L 62.594 0 L 60.936 1.84 L 77.286 16.561 Z M 86.404 14.72 L 70.055 0 L 68.397 1.84 L 84.747 16.561 Z M 93.865 14.72 L 77.516 0 L 75.858 1.84 L 92.208 16.561 Z M 48.85 14.72 L 32.5 0 L 30.842 1.84 L 47.192 16.561 Z M 41.138 14.72 L 24.79 0 L 23.131 1.84 L 39.481 16.561 Z M 33.428 14.72 L 17.078 0 L 15.421 1.84 L 31.771 16.561 Z M 25.717 14.72 L 9.367 0 L 7.711 1.84 L 24.058 16.562 Z M 18.006 14.72 L 1.656 0 L 0 1.84 L 16.348 16.562 Z";

const BRACKET_PATH = "M 0 21 L 0 41 L 18 59 L 85.5 59 M 0 11 L 0 0";

const RIGHT_LINE_PATH =
  "M 3 291 L 0 291 L 0 281 L 3 281 Z M 3 262 L 0 262 L 0 0 L 3 0 Z";

const POINTER_PATH = "M 0 20 L 8.5 0 L 17 20 L 8.5 16.923 Z";

const AGENT_BOLT_PATH =
  "M 15.74 1.317 C 16.043 0.238 14.996 -0.4 14.04 0.281 L 0.693 9.79 C -0.344 10.528 -0.181 12 0.938 12 L 4.453 12 L 4.453 11.973 L 11.303 11.973 L 5.721 13.943 L 3.261 22.683 C 2.957 23.762 4.004 24.4 4.96 23.719 L 18.307 14.21 C 19.344 13.472 19.181 12 18.062 12 L 12.732 12 Z";

const UNDO_PATH =
  "M 3.425 0.182 C 3.575 0.027 3.798 -0.035 4.007 0.02 C 4.217 0.074 4.38 0.238 4.435 0.447 C 4.49 0.656 4.427 0.879 4.272 1.029 L 2.296 3.006 L 8.849 3.006 C 10.561 3.006 11.949 4.394 11.949 6.106 C 11.949 7.819 10.561 9.206 8.849 9.206 L 6.849 9.206 C 6.518 9.206 6.249 8.938 6.249 8.606 C 6.249 8.275 6.518 8.006 6.849 8.006 L 8.849 8.006 C 9.898 8.006 10.749 7.156 10.749 6.106 C 10.749 5.057 9.898 4.206 8.849 4.206 L 2.296 4.206 L 4.272 6.182 C 4.427 6.333 4.49 6.555 4.435 6.765 C 4.38 6.974 4.217 7.137 4.007 7.192 C 3.798 7.247 3.575 7.185 3.425 7.029 L 0.425 4.029 L 0 3.606 L 0.425 3.182 Z";

const REDO_PATH =
  "M 8.524 0.182 C 8.374 0.027 8.151 -0.035 7.942 0.02 C 7.732 0.074 7.569 0.238 7.514 0.447 C 7.459 0.656 7.522 0.879 7.677 1.029 L 9.653 3.006 L 3.1 3.006 C 1.388 3.006 0 4.394 0 6.106 C 0 7.819 1.388 9.206 3.1 9.206 L 5.1 9.206 C 5.431 9.206 5.7 8.938 5.7 8.606 C 5.7 8.275 5.431 8.006 5.1 8.006 L 3.1 8.006 C 2.051 8.006 1.2 7.156 1.2 6.106 C 1.2 5.057 2.051 4.206 3.1 4.206 L 9.653 4.206 L 7.677 6.182 C 7.522 6.333 7.459 6.555 7.514 6.765 C 7.569 6.974 7.732 7.137 7.942 7.192 C 8.151 7.247 8.374 7.185 8.524 7.029 L 11.524 4.029 L 11.949 3.606 L 11.524 3.182 Z";

const SPARKLE_PATH =
  "M 3.994 3.091 C 4.854 4.822 6.256 6.225 7.987 7.085 C 6.256 7.945 4.854 9.347 3.994 11.078 C 3.134 9.347 1.731 7.945 0 7.085 C 1.731 6.225 3.134 4.822 3.994 3.091 Z M 10.494 0 C 10.884 0.65 11.428 1.194 12.078 1.585 C 11.428 1.975 10.884 2.519 10.494 3.169 C 10.103 2.519 9.559 1.975 8.909 1.585 C 9.559 1.195 10.104 0.65 10.494 0 Z";

const CANVAS_GLYPH_PATH =
  "M 7.994 0.068 C 9.106 -0.222 10.243 0.443 10.534 1.555 L 10.577 1.764 C 10.739 2.818 10.079 3.825 9.047 4.096 L 8.837 4.139 C 8.07 4.257 7.301 3.939 6.841 3.314 C 5.843 3.579 5.247 4.6 5.508 5.599 L 6.321 8.709 C 6.75 10.353 5.772 12.035 4.131 12.475 C 4.028 13.323 3.418 14.022 2.592 14.238 L 2.383 14.281 C 1.293 14.44 0.267 13.722 0.044 12.644 C -0.18 11.565 0.475 10.499 1.539 10.211 C 2.588 9.937 3.672 10.515 4.029 11.539 C 5.103 11.173 5.712 10.041 5.426 8.943 L 4.613 5.833 C 4.237 4.392 5.055 2.91 6.475 2.46 C 6.276 1.388 6.938 0.344 7.993 0.068 Z M 1.772 11.105 C 1.366 11.205 1.045 11.515 0.933 11.917 C 0.821 12.32 0.934 12.752 1.229 13.047 C 1.525 13.342 1.956 13.456 2.359 13.343 C 2.967 13.173 3.327 12.546 3.166 11.935 C 3.006 11.324 2.385 10.955 1.772 11.105 Z M 8.227 0.963 C 7.815 1.057 7.487 1.367 7.371 1.774 C 7.255 2.18 7.369 2.617 7.67 2.914 C 7.97 3.211 8.409 3.321 8.814 3.2 C 9.412 3.022 9.761 2.401 9.602 1.798 C 9.444 1.194 8.835 0.825 8.227 0.963 Z";

const PLUS_PATH =
  "M 8 4 C 8 4.184 7.851 4.333 7.667 4.333 L 4.333 4.333 L 4.333 7.667 C 4.333 7.851 4.184 8 4 8 C 3.816 8 3.667 7.851 3.667 7.667 L 3.667 4.333 L 0.333 4.333 C 0.149 4.333 0 4.184 0 4 C 0 3.816 0.149 3.667 0.333 3.667 L 3.667 3.667 L 3.667 0.333 C 3.667 0.149 3.816 0 4 0 C 4.184 0 4.333 0.149 4.333 0.333 L 4.333 3.667 L 7.667 3.667 C 7.851 3.667 8 3.816 8 4 Z";

// The eight graph node marks, lifted from inspo/armory.framer.ai.html:
// #email .framer-nzv9ml, #if .framer-xzfi5, #fields .framer-1q2wd4b,
// #code .framer-qc76h9, #fields1 .framer-1rvvrd7, #send .framer-1cv07le,
// #telegram .framer-1925229. Framer paints each as a background at
// `background-size: 100% 100%`, hence `preserveAspectRatio="none"` against the
// box width/height the reference gives the element.
type NodeMarkName = "email" | "fields" | "code" | "if" | "telegram" | "send";

type NodeMarkDef = {
  viewBox: string;
  width: number;
  height: number;
  d: string;
};

const NODE_MARKS: Record<NodeMarkName, NodeMarkDef> = {
  email: {
    viewBox: "0 0 17.333 16",
    width: 18,
    height: 16,
    d: "M 17.037 5.445 L 9.037 0.112 C 8.813 -0.037 8.521 -0.037 8.297 0.112 L 0.297 5.445 C 0.111 5.569 0 5.777 0 6 L 0 14.667 C 0 15.403 0.597 16 1.333 16 L 16 16 C 16.736 16 17.333 15.403 17.333 14.667 L 17.333 6 C 17.333 5.777 17.222 5.569 17.037 5.445 Z M 6.06 10.667 L 1.333 14 L 1.333 7.295 Z M 7.424 11.334 L 9.909 11.334 L 14.628 14.667 L 2.705 14.667 Z M 11.273 10.667 L 16 7.295 L 16 14 Z",
  },
  fields: {
    viewBox: "0 0 16.001 16",
    width: 16,
    height: 16,
    d: "M 15.626 3.949 L 12.05 0.375 C 11.81 0.135 11.485 0 11.145 0 C 10.806 0 10.48 0.135 10.24 0.375 L 0.375 10.24 C 0.134 10.479 -0.001 10.805 0 11.145 L 0 14.72 C 0 15.427 0.573 16 1.28 16 L 14.72 16 C 15.073 16 15.36 15.713 15.36 15.36 C 15.36 15.006 15.073 14.72 14.72 14.72 L 6.666 14.72 L 15.626 5.76 C 15.866 5.52 16.001 5.194 16.001 4.855 C 16.001 4.515 15.866 4.189 15.626 3.949 Z M 12.8 6.775 L 9.226 3.2 L 11.146 1.28 L 14.72 4.855 Z",
  },
  code: {
    viewBox: "0 0 18.909 16",
    width: 19,
    height: 16,
    d: "M 17.455 0 L 1.455 0 C 0.651 0 0 0.651 0 1.455 L 0 14.545 C 0 15.349 0.651 16 1.455 16 L 17.455 16 C 18.258 16 18.909 15.349 18.909 14.545 L 18.909 1.455 C 18.909 0.651 18.258 0 17.455 0 Z M 6.255 9.6 C 6.576 9.841 6.641 10.297 6.4 10.618 C 6.159 10.94 5.703 11.005 5.382 10.764 L 2.473 8.582 C 2.29 8.444 2.182 8.229 2.182 8 C 2.182 7.771 2.29 7.556 2.473 7.418 L 5.382 5.236 C 5.703 4.995 6.159 5.06 6.4 5.382 C 6.641 5.703 6.576 6.159 6.255 6.4 L 4.121 8 Z M 11.608 3.109 L 8.699 13.291 C 8.632 13.545 8.432 13.743 8.177 13.809 C 7.922 13.874 7.652 13.797 7.47 13.607 C 7.288 13.416 7.224 13.142 7.301 12.891 L 10.21 2.709 C 10.326 2.33 10.724 2.114 11.105 2.223 C 11.486 2.332 11.71 2.726 11.608 3.109 Z M 16.436 8.582 L 13.527 10.764 C 13.206 11.005 12.75 10.94 12.509 10.618 C 12.268 10.297 12.333 9.841 12.655 9.6 L 14.788 8 L 12.655 6.4 C 12.333 6.159 12.268 5.703 12.509 5.382 C 12.75 5.06 13.206 4.995 13.527 5.236 L 16.436 7.418 C 16.619 7.556 16.727 7.771 16.727 8 C 16.727 8.229 16.619 8.444 16.436 8.582 Z",
  },
  if: {
    viewBox: "0 0 20.466 19",
    width: 21,
    height: 19,
    d: "M 20.279 8.527 L 17.203 11.939 C 16.926 12.247 16.531 12.423 16.117 12.423 L 10.231 12.423 L 10.231 18.269 C 10.231 18.673 9.904 19 9.5 19 C 9.096 19 8.769 18.673 8.769 18.269 L 8.769 12.423 L 1.462 12.423 C 0.654 12.423 0 11.769 0 10.962 L 0 5.115 C 0 4.308 0.654 3.654 1.462 3.654 L 8.769 3.654 L 8.769 0.731 C 8.769 0.327 9.096 0 9.5 0 C 9.904 0 10.231 0.327 10.231 0.731 L 10.231 3.654 L 16.117 3.654 C 16.531 3.654 16.926 3.83 17.203 4.138 L 20.279 7.55 C 20.529 7.828 20.529 8.249 20.279 8.527 Z",
  },
  telegram: {
    viewBox: "0 0 17.92 16",
    width: 18,
    height: 16,
    d: "M 17.671 0.175 C 17.469 0.001 17.187 -0.048 16.938 0.049 L 0.725 6.394 C 0.253 6.578 -0.041 7.051 0.005 7.556 C 0.05 8.06 0.423 8.474 0.92 8.571 L 5.121 9.396 L 5.121 14.08 C 5.119 14.602 5.436 15.072 5.921 15.266 C 6.404 15.464 6.96 15.346 7.321 14.968 L 9.347 12.867 L 12.561 15.68 C 12.792 15.886 13.091 15.999 13.401 16 C 13.537 16 13.672 15.978 13.801 15.937 C 14.231 15.8 14.556 15.446 14.655 15.006 L 17.902 0.88 C 17.961 0.62 17.872 0.349 17.671 0.175 Z M 13.403 14.72 L 6.789 8.92 L 16.309 2.097 Z",
  },
  send: {
    viewBox: "0 0 14.282 15.997",
    width: 15,
    height: 16,
    d: "M 14.282 7.989 C 14.283 8.404 14.06 8.786 13.698 8.989 L 1.704 15.847 C 1.532 15.944 1.337 15.996 1.139 15.997 C 0.77 15.995 0.424 15.814 0.211 15.511 C -0.002 15.209 -0.055 14.822 0.068 14.473 L 1.997 8.763 C 2.035 8.648 2.142 8.57 2.262 8.568 L 7.425 8.568 C 7.584 8.569 7.735 8.503 7.843 8.388 C 7.952 8.272 8.007 8.117 7.997 7.959 C 7.97 7.654 7.713 7.422 7.407 7.425 L 2.264 7.425 C 2.141 7.425 2.032 7.347 1.993 7.231 L 0.064 1.521 C -0.093 1.073 0.044 0.574 0.407 0.269 C 0.77 -0.037 1.285 -0.087 1.699 0.145 L 13.699 6.994 C 14.059 7.196 14.282 7.576 14.282 7.989 Z",
  },
};

// Four 55x55 isometric tile marks, lifted from inspo/armory.framer.ai.html
// symbols #30946997 #500905072 #2958793995 #3520920769
type TileIconPart = {
  d: string;
  fill: string;
  stroke?: string;
  strokeWidth?: string;
  transform?: string;
};

type TileIconSet = ReadonlyArray<TileIconPart>;

const TILE_ICONS: ReadonlyArray<TileIconSet> = [
  [
    { d: "M 0 0.504 L 0.873 0 L 35.514 20 L 34.641 20.504 Z", fill: "rgb(26, 26, 26)", transform: "translate(1.492 21.706)" },
    { d: "M 34.641 0 L 35.514 0.504 L 0.873 20.504 L 0 20 Z", fill: "rgb(26, 26, 26)", transform: "translate(1.492 11.79)" },
    { d: "M 0 0.504 L 0.873 0 L 35.514 20 L 34.641 20.504 Z", fill: "rgb(26, 26, 26)", transform: "translate(19.249 11.454)" },
    { d: "M 34.641 0 L 35.514 0.504 L 0.873 20.504 L 0 20 Z", fill: "rgb(26, 26, 26)", transform: "translate(19.249 22.042)" },
    { d: "M 0 0.504 L 0.873 0 L 35.514 20 L 34.641 20.504 Z", fill: "rgb(26, 26, 26)", transform: "translate(10.516 16.496)" },
    { d: "M 34.641 0 L 35.514 0.504 L 0.873 20.504 L 0 20 Z", fill: "rgb(26, 26, 26)", transform: "translate(10.516 17)" },
    { d: "M 0 0.504 L 0.873 0 L 35.514 20 L 34.641 20.504 Z", fill: "rgb(26, 26, 26)", transform: "translate(14.882 13.975)" },
    { d: "M 34.641 0 L 35.514 0.504 L 0.873 20.504 L 0 20 Z", fill: "rgb(26, 26, 26)", transform: "translate(14.882 19.521)" },
    { d: "M 0 0.504 L 0.873 0 L 35.514 20 L 34.641 20.504 Z", fill: "rgb(26, 26, 26)", transform: "translate(6.149 19.017)" },
    { d: "M 34.641 0 L 35.514 0.504 L 0.873 20.504 L 0 20 Z", fill: "rgb(26, 26, 26)", transform: "translate(6.149 14.479)" },
    { d: "M 26.49 30.588 L 0 15.294 L 26.49 0 L 52.98 15.294 Z", fill: "rgb(255, 255, 255)", stroke: "rgb(6, 6, 6)", strokeWidth: "0.336134", transform: "translate(1.213 11.706)" },
    { d: "M 39.59 0 L 19.795 11.429 L 0 0 L 0 6.387 L 19.795 17.815 L 39.59 6.387 Z", fill: "rgb(26, 26, 26)", transform: "translate(7.984 20.95)" },
    { d: "M 0 11.429 L 19.795 0 L 39.59 11.429 L 39.59 17.815 L 19.795 6.387 L 0 17.815 Z", fill: "rgb(26, 26, 26)", transform: "translate(7.984 9.521)" },
    { d: "M 38.917 11.235 L 19.459 22.47 L 0 11.235 L 19.459 0 Z", fill: "rgb(255, 255, 255)", stroke: "rgb(6, 6, 6)", strokeWidth: "0.336134", transform: "translate(8.32 9.714)" },
    { d: "M 9.461 3.95 C 10.345 4.46 10.345 5.288 9.461 5.798 C 8.676 6.251 7.459 6.302 6.577 5.951 L 3.34 7.819 L 4.652 9.788 L 4.796 10.004 L 0.873 12.269 L 0 11.765 L 3.355 9.828 L 2.043 7.859 L 1.899 7.643 L 5.787 5.399 C 5.423 4.92 5.58 4.342 6.259 3.95 C 7.143 3.439 8.576 3.439 9.461 3.95 Z M 9.744 14.542 L 11.056 16.511 L 11.2 16.727 L 9.897 17.479 L 9.024 16.975 L 9.759 16.551 L 8.448 14.582 L 8.304 14.366 L 11.207 12.689 L 12.081 13.193 Z M 14.701 11.008 C 15.585 11.519 15.585 12.347 14.701 12.857 C 13.816 13.368 12.383 13.368 11.498 12.857 C 10.614 12.347 10.614 11.519 11.498 11.008 C 12.383 10.498 13.816 10.498 14.701 11.008 Z M 13.827 11.513 C 13.425 11.281 12.774 11.281 12.372 11.513 C 11.97 11.745 11.97 12.121 12.372 12.353 C 12.774 12.585 13.425 12.585 13.827 12.353 C 14.229 12.121 14.229 11.745 13.827 11.513 Z M 15.276 3.113 L 16.587 5.082 L 16.731 5.298 L 14.203 6.758 C 14.685 7.255 14.56 7.896 13.827 8.319 C 12.943 8.83 11.509 8.83 10.625 8.319 C 9.741 7.809 9.741 6.981 10.625 6.471 C 11.358 6.048 12.468 5.975 13.33 6.254 L 15.29 5.122 L 13.979 3.153 L 13.835 2.937 L 18.922 0 L 19.795 0.504 Z M 8.588 4.454 C 8.186 4.222 7.534 4.222 7.132 4.454 C 6.73 4.686 6.73 5.062 7.132 5.294 C 7.534 5.526 8.186 5.526 8.588 5.294 C 8.989 5.062 8.989 4.686 8.588 4.454 Z M 12.954 6.975 C 12.552 6.743 11.9 6.743 11.498 6.975 C 11.097 7.207 11.097 7.583 11.498 7.815 C 11.9 8.047 12.552 8.047 12.954 7.815 C 13.356 7.583 13.356 7.207 12.954 6.975 Z M 27.953 7.055 L 27.578 6.972 L 24.169 6.214 L 21.951 7.495 C 22.56 8.004 22.472 8.707 21.687 9.16 C 20.803 9.67 19.369 9.67 18.485 9.16 C 17.601 8.649 17.601 7.822 18.485 7.311 C 19.164 6.919 20.166 6.828 20.995 7.038 L 23.863 5.383 L 24.238 5.466 L 27.648 6.223 L 28.382 5.798 L 29.256 6.303 Z M 20.814 7.815 C 20.412 7.583 19.76 7.583 19.358 7.815 C 18.956 8.047 18.956 8.423 19.358 8.656 C 19.76 8.888 20.412 8.888 20.814 8.656 C 21.216 8.423 21.216 8.047 20.814 7.815 Z", fill: "rgb(6, 6, 6)", transform: "translate(13.367 12.211)" },
  ],
  [
    { d: "M 52.578 0 L 23.522 16.776 L 0 3.195 L 0 5.991 L 23.522 19.572 L 52.578 2.796 Z", fill: "rgb(26, 26, 26)", transform: "translate(1 29.366)" },
    { d: "M 0 18.972 L 32.861 0 L 51.713 10.884 L 52.578 15.777 L 52.578 18.573 L 51.713 13.68 L 32.861 2.796 L 0 21.768 Z", fill: "rgb(26, 26, 26)", transform: "translate(1 13.589)" },
    { d: "M 50.941 10.746 L 51.751 15.333 L 22.99 31.939 L 0 18.665 L 32.329 0 Z", fill: "rgb(255, 255, 255)", stroke: "rgb(6, 6, 6)", strokeWidth: "0.532558", transform: "translate(1.531 13.896)" },
    { d: "M 12.799 21.868 L 0 14.479 L 1.038 13.88 L 13.836 21.269 Z M 14.182 18.273 L 3.805 12.282 L 4.843 11.683 L 15.22 17.674 Z M 20.063 21.668 L 16.604 19.671 L 17.641 19.072 L 21.1 21.069 Z M 23.868 19.472 L 10.377 11.683 L 11.415 11.084 L 24.905 18.873 Z M 27.673 17.275 L 11.415 7.889 L 12.453 7.289 L 28.71 16.676 Z M 18.333 7.489 L 15.22 5.692 L 16.258 5.093 L 19.371 6.89 Z M 31.478 15.078 L 20.408 8.687 L 21.446 8.088 L 32.515 14.479 Z M 30.094 9.886 L 19.025 3.495 L 20.063 2.896 L 31.132 9.287 Z M 35.282 12.881 L 32.169 11.084 L 33.207 10.485 L 36.32 12.282 Z M 27.327 3.695 L 23.003 1.198 L 25.078 0 L 29.402 2.496 Z M 39.72 9.486 L 44.158 11.883 L 43.45 12.082 L 38.623 9.486 L 43.27 6.557 L 43.714 7.001 Z", fill: "rgb(6, 6, 6)", transform: "translate(8.586 17.583)" },
    { d: "M 52.578 0 L 23.522 16.776 L 0 3.195 L 0 5.991 L 23.522 19.572 L 52.578 2.796 Z", fill: "rgb(26, 26, 26)", transform: "translate(1 21.777)" },
    { d: "M 0 18.972 L 32.861 0 L 51.713 10.884 L 52.578 15.777 L 52.578 18.573 L 51.713 13.68 L 32.861 2.796 L 0 21.768 Z", fill: "rgb(26, 26, 26)", transform: "translate(1 6)" },
    { d: "M 50.941 10.746 L 51.751 15.333 L 22.99 31.938 L 0 18.665 L 32.329 0 Z", fill: "rgb(255, 255, 255)", stroke: "rgb(6, 6, 6)", strokeWidth: "0.532558", transform: "translate(1.531 6.307)" },
    { d: "M 12.799 21.868 L 0 14.479 L 1.038 13.88 L 13.836 21.269 Z M 14.182 18.273 L 3.805 12.282 L 4.843 11.683 L 15.22 17.674 Z M 20.063 21.668 L 16.604 19.671 L 17.641 19.072 L 21.1 21.069 Z M 23.868 19.472 L 10.377 11.683 L 11.415 11.084 L 24.905 18.873 Z M 27.673 17.275 L 11.415 7.889 L 12.453 7.289 L 28.71 16.676 Z M 18.333 7.489 L 15.22 5.692 L 16.258 5.093 L 19.371 6.89 Z M 31.478 15.078 L 20.408 8.687 L 21.446 8.088 L 32.515 14.479 Z M 30.094 9.886 L 19.025 3.495 L 20.063 2.896 L 31.132 9.286 Z M 35.282 12.881 L 32.169 11.084 L 33.207 10.485 L 36.32 12.282 Z M 27.327 3.695 L 23.003 1.198 L 25.078 0 L 29.402 2.496 Z M 39.104 9.286 L 44.158 11.927 L 43.601 12.282 L 38.066 9.286 L 42.827 6.601 L 43.27 6.89 Z", fill: "rgb(6, 6, 6)", transform: "translate(8.586 9.994)" },
  ],
  [
    { d: "M 54 0 L 27 15.588 L 0 0 L 0 4.197 L 27 19.785 L 54 4.197 Z", fill: "rgb(26, 26, 26)", transform: "translate(1 31.499)" },
    { d: "M 53.28 15.38 L 26.641 30.761 L 0 15.38 L 26.641 0 Z", fill: "rgb(255, 255, 255)", stroke: "rgb(6, 6, 6)", strokeWidth: "0.36", transform: "translate(1.359 16.118)" },
    { d: "M 42.109 0 L 21.055 12.156 L 0 0 L 0 3.273 L 21.055 15.429 L 42.109 3.273 Z", fill: "rgb(26, 26, 26)", transform: "translate(6.828 28.065)" },
    { d: "M 41.39 11.947 L 20.695 23.896 L 0 11.947 L 20.695 0 Z", fill: "rgb(255, 255, 255)", stroke: "rgb(6, 6, 6)", strokeWidth: "0.36", transform: "translate(7.188 16.117)" },
    { d: "M 26.987 7.711 L 13.494 15.422 L 0 7.711 L 13.494 0 Z", fill: "rgb(255, 255, 255)", stroke: "rgb(6, 6, 6)", strokeWidth: "0.818182", transform: "translate(14.199 20.471)" },
    { d: "M 0 0 L 0 4.5", fill: "transparent", stroke: "rgb(6, 6, 6)", strokeWidth: "0.818182", transform: "translate(28.922 26.546)" },
    { d: "M 2.455 0 L 0 2.455", fill: "transparent", stroke: "rgb(6, 6, 6)", strokeWidth: "0.818182", transform: "translate(21.961 24.909)" },
    { d: "M 0.538 0 C -1.098 6.545 0.047 19.8 17.72 20.455 C 17.72 9 10.764 2.455 0.538 0 Z", fill: "rgb(6, 6, 6)", stroke: "rgb(6, 6, 6)", strokeWidth: "0.36", transform: "translate(20.602 9.772)" },
    { d: "M 1.815 1.107 C 3.445 -0.254 5.935 -0.331 8.684 0.716 C 11.427 1.76 14.386 3.911 16.904 6.927 C 19.423 9.943 21.011 13.239 21.549 16.123 C 22.089 19.015 21.569 21.451 19.938 22.812 C 18.308 24.173 15.819 24.25 13.07 23.203 C 10.328 22.159 7.367 20.009 4.849 16.993 C 2.331 13.977 0.744 10.681 0.205 7.796 C -0.334 4.904 0.185 2.468 1.815 1.107 Z", fill: "rgb(255, 255, 255)", stroke: "rgb(6, 6, 6)", strokeWidth: "0.36", transform: "translate(21.114 6.181)" },
    { d: "M 0 17.591 L 2.045 0", fill: "transparent", stroke: "rgb(6, 6, 6)", strokeWidth: "0.818182", transform: "translate(35.469 11)" },
    { d: "M 2.046 9 L 0 0", fill: "transparent", stroke: "rgb(6, 6, 6)", strokeWidth: "0.818182", transform: "translate(37.516 11.409)" },
    { d: "M 0 0 L 8.182 2.046", fill: "transparent", stroke: "rgb(6, 6, 6)", strokeWidth: "0.818182", transform: "translate(28.922 8.546)" },
    { d: "M 0 1.227 L 14.318 0", fill: "transparent", stroke: "rgb(6, 6, 6)", strokeWidth: "0.818182", transform: "translate(23.195 10.591)" },
    { d: "M 3.274 1.569 L 1.248 3.218 C 0.539 3.128 0.241 2.826 0.109 2.528 C -0.023 2.231 -0.006 1.905 0.013 1.694 L 1.678 0 Z", fill: "rgb(255, 255, 255)", stroke: "rgb(6, 6, 6)", strokeWidth: "0.36", transform: "translate(35.803 9.523)" },
    { d: "M 0.423 1.303 C 0.202 1.039 0.066 0.757 0.019 0.522 C -0.03 0.279 0.022 0.13 0.103 0.062 C 0.184 -0.006 0.341 -0.032 0.571 0.058 C 0.795 0.145 1.049 0.328 1.271 0.591 C 1.492 0.855 1.628 1.137 1.675 1.373 C 1.723 1.615 1.671 1.765 1.59 1.833 C 1.508 1.901 1.353 1.926 1.122 1.837 C 0.899 1.75 0.645 1.567 0.423 1.303 Z", fill: "rgb(255, 255, 255)", stroke: "rgb(6, 6, 6)", strokeWidth: "0.36", transform: "translate(37.52 9.315)" },
  ],
  [
    { d: "M 0 17.706 L 30.668 0 L 53.49 13.177 L 53.49 17.294 L 30.668 4.118 L 0 21.824 Z", fill: "rgb(255, 255, 255)", stroke: "rgb(6, 6, 6)", strokeWidth: "0.24706", transform: "translate(1 11)" },
    { d: "M 53.49 0 L 22.823 17.706 L 0 4.529 L 0 8.647 L 22.823 21.824 L 53.49 4.118 Z", fill: "rgb(26, 26, 26)", transform: "translate(1 24.177)" },
    { d: "M 52.765 12.967 L 22.46 30.464 L 0 17.496 L 30.305 0 Z", fill: "rgb(255, 255, 255)", stroke: "rgb(6, 6, 6)", strokeWidth: "0.362355", transform: "translate(1.362 11.209)" },
    { d: "M 16.824 0 L 20.706 2.238 L 16.824 4.475 L 12.941 2.238 Z M 20.928 3.317 C 18.386 2.731 16.513 2.837 15.94 2.947 L 15.578 3.016 L 15.579 2.797 L 15.579 1.305 L 16.127 1.305 L 16.127 2.594 C 17.042 2.496 18.853 2.498 21.131 3.023 Z M 22.647 4.102 L 36.235 11.933 L 35.588 12.306 L 22 4.475 Z M 20.706 5.221 L 27.177 8.95 L 26.53 9.323 L 20.059 5.594 Z M 10.353 3.729 L 14.235 5.967 L 10.353 8.204 L 6.471 5.967 Z M 14.457 7.046 C 11.916 6.46 10.042 6.566 9.47 6.676 L 9.108 6.746 L 9.108 6.526 L 9.108 5.034 L 9.657 5.034 L 9.657 6.324 C 10.571 6.225 12.382 6.227 14.661 6.752 Z M 16.177 7.831 L 29.765 15.663 L 29.118 16.036 L 15.529 8.204 Z M 14.235 8.95 L 20.706 12.679 L 20.059 13.052 L 13.588 9.323 Z M 3.882 7.458 L 7.765 9.696 L 3.882 11.933 L 0 9.696 Z M 7.987 10.775 C 5.445 10.189 3.572 10.295 2.999 10.405 L 2.637 10.475 L 2.638 10.255 L 2.638 8.764 L 3.186 8.764 L 3.186 10.053 C 4.101 9.954 5.911 9.956 8.19 10.482 Z M 9.706 11.561 L 23.294 19.392 L 22.647 19.765 L 9.059 11.934 Z M 7.765 12.679 L 14.235 16.409 L 13.588 16.781 L 7.118 13.052 Z", fill: "rgb(255, 255, 255)", stroke: "rgb(6, 6, 6)", strokeWidth: "0.362355", transform: "translate(6.961 12.646)" },
  ],
];

// Six STACK rail glyphs, lifted from inspo/armory.framer.ai.html symbols
// #svg-1145068834_2553 #svg426909933_2525 #svg-328414571_397
// #svg313257761_5177 #svg871512924_1358 #svg-1443400659_533
type StackIconDef = {
  viewBox: string;
  width: number;
  height: number;
  paths: ReadonlyArray<string>;
};

const STACK_ICONS: ReadonlyArray<StackIconDef> = [
  {
    viewBox: "0 0 15.124 15.124",
    width: 15,
    height: 15,
    paths: [
      "M 14.128 6.189 C 15.252 7.411 15.446 9.225 14.604 10.656 C 14.054 11.604 13.124 12.269 12.05 12.484 C 11.534 14.061 10.044 15.128 8.364 15.124 C 7.267 15.133 6.219 14.672 5.484 13.858 C 3.848 14.208 2.172 13.471 1.325 12.029 C 0.769 11.095 0.649 9.964 0.997 8.934 C -0.128 7.712 -0.321 5.898 0.522 4.466 C 1.071 3.52 2.001 2.855 3.074 2.64 C 3.59 1.063 5.08 -0.003 6.758 0 C 7.855 -0.008 8.904 0.453 9.64 1.267 C 11.275 0.918 12.95 1.655 13.798 3.096 C 14.353 4.03 14.474 5.16 14.127 6.19 Z M 8.365 14.135 C 9.949 14.131 11.233 12.863 11.235 11.3 L 11.235 7.785 C 11.233 7.771 11.224 7.757 11.21 7.751 L 9.919 7.016 L 9.919 11.261 C 9.919 11.439 9.824 11.603 9.669 11.689 L 6.616 13.429 C 6.59 13.445 6.55 13.467 6.526 13.48 C 7.044 13.906 7.694 14.137 8.365 14.136 Z M 2.194 11.534 C 2.988 12.889 4.743 13.353 6.118 12.572 L 9.203 10.816 C 9.215 10.807 9.222 10.792 9.222 10.776 L 9.222 9.306 L 5.496 11.43 C 5.34 11.518 5.149 11.518 4.994 11.43 L 1.941 9.69 L 1.851 9.636 C 1.735 10.29 1.857 10.964 2.194 11.536 Z M 1.39 4.961 L 1.389 4.962 C 1.01 5.614 0.908 6.39 1.105 7.117 C 1.303 7.844 1.784 8.462 2.441 8.832 L 5.527 10.592 C 5.541 10.598 5.557 10.597 5.57 10.588 L 6.86 9.853 L 3.134 7.73 C 2.978 7.643 2.883 7.479 2.884 7.301 L 2.884 3.822 L 2.885 3.718 C 2.256 3.944 1.727 4.384 1.39 4.96 Z M 11.988 7.395 C 12.143 7.482 12.238 7.646 12.238 7.823 L 12.238 11.406 C 12.866 11.179 13.394 10.74 13.733 10.164 C 14.113 9.513 14.216 8.736 14.018 8.007 C 13.821 7.279 13.339 6.661 12.682 6.291 L 9.596 4.533 C 9.581 4.527 9.565 4.529 9.553 4.537 L 8.263 5.272 L 11.989 7.393 Z M 13.272 5.487 C 13.271 5.487 13.271 5.487 13.271 5.487 C 13.271 5.487 13.271 5.488 13.271 5.488 Z M 13.271 5.487 C 13.384 4.835 13.262 4.165 12.928 3.593 C 12.136 2.236 10.379 1.771 9.004 2.553 L 5.919 4.309 C 5.907 4.318 5.9 4.333 5.901 4.348 L 5.901 5.818 L 9.629 3.694 C 9.783 3.605 9.974 3.605 10.129 3.694 L 13.182 5.433 C 13.212 5.451 13.241 5.468 13.271 5.487 Z M 5.201 8.107 L 5.2 8.107 L 5.2 8.107 Z M 5.2 8.107 L 5.203 3.863 C 5.202 3.685 5.298 3.521 5.454 3.435 L 8.506 1.695 C 8.535 1.678 8.565 1.661 8.596 1.645 C 8.078 1.221 7.429 0.989 6.76 0.989 C 5.173 0.988 3.886 2.256 3.886 3.822 L 3.886 7.337 C 3.887 7.352 3.897 7.365 3.911 7.372 Z M 5.901 8.506 L 7.561 9.452 L 9.221 8.506 L 9.221 6.616 L 7.561 5.671 L 5.901 6.616 Z",
    ],
  },
  {
    viewBox: "0 0 17.138 16.893",
    width: 17,
    height: 17,
    paths: [
      "M 3.363 11.23 L 6.733 9.367 L 6.79 9.205 L 6.733 9.115 L 6.57 9.115 L 6.005 9.082 L 4.08 9.03 L 2.41 8.962 L 0.79 8.876 L 0.383 8.791 L 0 8.295 L 0.04 8.047 L 0.382 7.821 L 0.872 7.863 L 1.957 7.936 L 3.584 8.046 L 4.764 8.116 L 6.512 8.295 L 6.79 8.295 L 6.83 8.185 L 6.734 8.115 L 6.66 8.047 L 4.976 6.923 L 3.154 5.735 L 2.2 5.051 L 1.683 4.705 L 1.423 4.38 L 1.31 3.67 L 1.779 3.163 L 2.408 3.205 L 2.568 3.248 L 3.206 3.731 L 4.57 4.77 L 6.349 6.06 L 6.609 6.274 L 6.713 6.201 L 6.726 6.15 L 6.609 5.957 L 5.642 4.235 L 4.609 2.483 L 4.149 1.756 L 4.028 1.32 C 3.982 1.153 3.956 0.98 3.953 0.807 L 4.488 0.094 L 4.782 0 L 5.492 0.094 L 5.792 0.351 L 6.235 1.346 L 6.951 2.915 L 8.061 5.048 L 8.387 5.68 L 8.56 6.265 L 8.625 6.445 L 8.738 6.445 L 8.738 6.342 L 8.83 5.142 L 8.999 3.667 L 9.163 1.77 L 9.22 1.235 L 9.489 0.595 L 10.022 0.248 L 10.439 0.445 L 10.782 0.927 L 10.734 1.24 L 10.53 2.542 L 10.13 4.586 L 9.87 5.953 L 10.022 5.953 L 10.196 5.783 L 10.899 4.863 L 12.079 3.41 L 12.6 2.833 L 13.207 2.197 L 13.597 1.893 L 14.335 1.893 L 14.878 2.688 L 14.635 3.508 L 13.875 4.457 L 13.246 5.261 L 12.344 6.457 L 11.78 7.415 L 11.832 7.492 L 11.966 7.478 L 14.006 7.052 L 15.107 6.854 L 16.422 6.633 L 17.017 6.906 L 17.082 7.184 L 16.847 7.752 L 15.441 8.094 L 13.793 8.419 L 11.337 8.991 L 11.307 9.013 L 11.342 9.056 L 12.448 9.158 L 12.921 9.184 L 14.079 9.184 L 16.235 9.342 L 16.8 9.709 L 17.138 10.159 L 17.082 10.499 L 16.214 10.936 L 15.043 10.662 L 12.309 10.022 L 11.372 9.79 L 11.242 9.79 L 11.242 9.868 L 12.022 10.62 L 13.455 11.894 L 15.246 13.534 L 15.337 13.94 L 15.107 14.26 L 14.865 14.226 L 13.29 13.06 L 12.682 12.534 L 11.307 11.394 L 11.216 11.394 L 11.216 11.514 L 11.533 11.97 L 13.207 14.449 L 13.294 15.209 L 13.173 15.457 L 12.739 15.607 L 12.262 15.521 L 11.28 14.166 L 10.27 12.641 L 9.454 11.274 L 9.354 11.33 L 8.873 16.436 L 8.647 16.696 L 8.127 16.893 L 7.693 16.569 L 7.463 16.043 L 7.693 15.004 L 7.971 13.65 L 8.196 12.573 L 8.4 11.235 L 8.521 10.791 L 8.513 10.761 L 8.413 10.774 L 7.389 12.158 L 5.832 14.231 L 4.599 15.53 L 4.304 15.645 L 3.792 15.385 L 3.84 14.919 L 4.126 14.504 L 5.831 12.367 L 6.86 11.043 L 7.524 10.278 L 7.519 10.167 L 7.48 10.167 L 2.95 13.064 L 2.144 13.167 L 1.796 12.846 L 1.839 12.321 L 2.004 12.15 L 3.367 11.226 Z",
    ],
  },
  {
    viewBox: "0 0 17.015 17.015",
    width: 17,
    height: 17,
    paths: [
      "M 8.507 17.015 C 7.842 12.621 4.394 9.173 0 8.507 C 4.393 7.842 7.842 4.393 8.507 0 C 9.173 4.393 12.622 7.841 17.015 8.507 C 12.621 9.173 9.173 12.621 8.507 17.015 Z",
    ],
  },
  {
    viewBox: "0 0 12.19 15.565",
    width: 12,
    height: 16,
    paths: [
      "M 3.159 0.064 C 3.314 0.124 3.453 0.223 3.58 0.354 C 3.792 0.57 3.97 0.88 4.107 1.247 C 4.244 1.617 4.333 2.026 4.367 2.437 C 4.82 2.185 5.32 2.032 5.836 1.987 L 5.872 1.983 C 6.496 1.933 7.112 2.045 7.65 2.319 C 7.723 2.356 7.794 2.396 7.863 2.439 C 7.899 2.036 7.987 1.637 8.122 1.276 C 8.258 0.908 8.436 0.599 8.647 0.381 C 8.766 0.256 8.91 0.157 9.069 0.091 C 9.251 0.019 9.452 0.009 9.64 0.061 C 9.928 0.142 10.174 0.322 10.369 0.583 C 10.547 0.822 10.68 1.127 10.771 1.493 C 10.936 2.154 10.965 3.024 10.853 4.073 L 10.892 4.101 L 10.91 4.114 C 11.453 4.522 11.83 5.103 12.03 5.777 C 12.343 6.829 12.186 8.009 11.648 8.669 L 11.635 8.684 L 11.637 8.686 C 11.935 9.226 12.117 9.795 12.156 10.384 L 12.157 10.405 C 12.203 11.159 12.014 11.917 11.573 12.662 L 11.568 12.669 L 11.576 12.686 C 11.914 13.505 12.02 14.329 11.89 15.153 L 11.885 15.18 C 11.865 15.301 11.798 15.409 11.697 15.48 C 11.571 15.569 11.409 15.59 11.264 15.537 C 11.119 15.483 11.01 15.363 10.97 15.213 C 10.956 15.155 10.953 15.095 10.962 15.035 C 11.082 14.304 10.97 13.571 10.618 12.825 C 10.552 12.684 10.563 12.519 10.647 12.388 L 10.65 12.384 C 11.083 11.73 11.262 11.089 11.223 10.46 C 11.19 9.909 10.991 9.367 10.65 8.852 C 10.584 8.752 10.56 8.63 10.584 8.512 C 10.608 8.394 10.679 8.291 10.779 8.225 L 10.785 8.221 C 10.96 8.108 11.12 7.821 11.201 7.428 C 11.291 6.964 11.267 6.485 11.133 6.032 C 10.986 5.536 10.717 5.123 10.341 4.841 C 9.914 4.52 9.349 4.365 8.634 4.409 C 8.444 4.421 8.266 4.318 8.181 4.147 C 7.983 3.71 7.644 3.353 7.218 3.131 C 6.825 2.938 6.385 2.856 5.948 2.896 C 5.055 2.966 4.268 3.463 4.033 4.089 C 3.965 4.272 3.79 4.392 3.595 4.389 C 2.83 4.391 2.238 4.568 1.805 4.887 C 1.433 5.166 1.164 5.561 1.041 6.01 C 0.919 6.445 0.902 6.903 0.991 7.345 C 1.072 7.739 1.229 8.066 1.409 8.242 L 1.415 8.247 C 1.567 8.392 1.6 8.622 1.493 8.803 C 1.235 9.243 1.042 9.899 1.01 10.529 C 0.975 11.249 1.144 11.875 1.526 12.323 L 1.537 12.337 C 1.655 12.472 1.682 12.663 1.606 12.825 C 1.193 13.699 1.066 14.418 1.203 14.985 C 1.228 15.103 1.205 15.225 1.139 15.325 C 1.001 15.536 0.721 15.6 0.504 15.472 C 0.401 15.411 0.325 15.312 0.293 15.195 C 0.118 14.475 0.237 13.65 0.632 12.72 L 0.642 12.695 L 0.636 12.687 C 0.442 12.405 0.297 12.092 0.207 11.761 L 0.204 11.747 C 0.096 11.336 0.053 10.91 0.077 10.485 C 0.108 9.841 0.277 9.181 0.523 8.652 L 0.531 8.634 L 0.53 8.632 C 0.307 8.303 0.153 7.931 0.078 7.54 L 0.075 7.523 C -0.044 6.939 -0.021 6.335 0.14 5.761 C 0.334 5.064 0.772 4.461 1.375 4.061 C 1.261 3.006 1.29 2.131 1.455 1.465 C 1.547 1.099 1.681 0.793 1.858 0.555 C 2.052 0.295 2.298 0.115 2.586 0.033 C 2.775 -0.02 2.976 -0.009 3.158 0.063 Z M 6.11 6.495 C 6.781 6.495 7.4 6.716 7.864 7.1 C 8.316 7.473 8.584 7.974 8.584 8.472 C 8.584 9.101 8.294 9.59 7.772 9.903 C 7.328 10.168 6.732 10.297 6.049 10.297 C 5.326 10.297 4.708 10.114 4.262 9.777 C 3.819 9.445 3.572 8.978 3.572 8.472 C 3.572 7.972 3.856 7.47 4.328 7.096 C 4.838 6.702 5.466 6.491 6.11 6.495 Z M 6.11 7.129 C 5.614 7.125 5.13 7.287 4.736 7.589 C 4.406 7.851 4.219 8.179 4.219 8.473 C 4.219 8.776 4.369 9.06 4.656 9.275 C 4.982 9.521 5.462 9.663 6.049 9.663 C 6.622 9.663 7.105 9.559 7.435 9.362 C 7.767 9.164 7.937 8.876 7.937 8.472 C 7.937 8.173 7.76 7.842 7.447 7.584 C 7.068 7.282 6.595 7.121 6.11 7.129 Z M 6.585 7.985 L 6.588 7.988 C 6.63 8.039 6.649 8.105 6.641 8.17 C 6.634 8.235 6.6 8.294 6.548 8.334 L 6.338 8.497 L 6.338 8.813 C 6.338 8.883 6.309 8.95 6.258 9 C 6.208 9.05 6.139 9.078 6.068 9.077 C 5.997 9.078 5.929 9.05 5.878 9 C 5.828 8.951 5.8 8.884 5.799 8.813 L 5.799 8.487 L 5.605 8.333 C 5.527 8.271 5.495 8.168 5.522 8.072 C 5.548 7.993 5.611 7.931 5.691 7.907 C 5.771 7.883 5.857 7.899 5.922 7.951 L 6.076 8.072 L 6.234 7.949 C 6.342 7.867 6.496 7.882 6.584 7.985 Z M 2.97 6.627 C 3.312 6.626 3.591 6.902 3.592 7.244 C 3.592 7.407 3.526 7.564 3.41 7.679 C 3.166 7.919 2.775 7.919 2.53 7.679 C 2.414 7.565 2.348 7.408 2.348 7.244 C 2.348 7.081 2.413 6.924 2.529 6.809 C 2.647 6.693 2.805 6.628 2.969 6.627 Z M 9.213 6.627 C 9.555 6.626 9.834 6.902 9.835 7.244 C 9.836 7.408 9.77 7.565 9.653 7.679 C 9.409 7.919 9.018 7.919 8.773 7.679 C 8.657 7.564 8.591 7.408 8.591 7.244 C 8.591 7.081 8.657 6.924 8.773 6.809 C 8.891 6.693 9.049 6.628 9.213 6.627 Z M 2.825 0.92 L 2.823 0.92 C 2.741 0.956 2.67 1.015 2.619 1.089 L 2.615 1.093 C 2.516 1.227 2.43 1.423 2.365 1.682 C 2.244 2.172 2.211 2.836 2.277 3.65 C 2.585 3.56 2.921 3.503 3.284 3.482 L 3.291 3.482 L 3.304 3.457 C 3.337 3.399 3.373 3.343 3.41 3.288 C 3.493 2.706 3.431 2.113 3.23 1.56 C 3.134 1.303 3.017 1.1 2.905 0.985 C 2.882 0.961 2.856 0.94 2.828 0.921 Z M 9.402 0.948 C 9.374 0.967 9.349 0.989 9.325 1.013 C 9.214 1.128 9.096 1.331 9.001 1.588 C 8.793 2.15 8.723 2.845 8.836 3.408 L 8.877 3.477 L 8.883 3.487 L 8.905 3.487 C 9.26 3.487 9.615 3.537 9.955 3.637 C 10.017 2.841 9.983 2.191 9.864 1.711 C 9.799 1.453 9.714 1.256 9.614 1.122 L 9.611 1.118 C 9.561 1.043 9.49 0.984 9.406 0.948 Z",
    ],
  },
  {
    viewBox: "0 0 15.288 13.17",
    width: 16,
    height: 13,
    paths: [
      "M 9.728 2.634 L 9.728 5.268 L 8.338 5.268 L 8.338 2.634 Z M 12.508 0 L 12.508 2.634 L 11.118 2.634 L 11.118 0 Z M 0 0 L 1.39 0 L 1.39 13.17 L 0 13.17 Z M 5.559 7.902 L 6.949 7.902 L 6.949 10.536 L 5.559 10.536 Z M 11.117 7.902 L 12.507 7.902 L 12.507 13.17 L 11.117 13.17 Z",
      "M 12.508 0 L 15.288 0 L 15.288 2.634 L 12.508 2.634 Z M 1.391 0 L 4.17 0 L 4.17 2.634 L 1.39 2.634 L 1.39 0 Z M 9.729 5.268 L 9.729 2.634 L 15.288 2.634 L 15.288 5.268 L 9.728 5.268 Z M 1.391 5.268 L 1.391 2.634 L 6.949 2.634 L 6.949 5.268 Z M 1.39 7.902 L 1.39 5.268 L 15.287 5.268 L 15.287 7.902 L 1.391 7.902 Z M 6.95 7.902 L 9.729 7.902 L 9.729 10.536 L 6.949 10.536 L 6.949 7.902 Z M 12.508 7.902 L 15.288 7.902 L 15.288 10.536 L 12.508 10.536 Z M 1.391 7.902 L 4.17 7.902 L 4.17 10.536 L 1.39 10.536 L 1.39 7.902 Z",
      "M 12.508 10.535 L 15.288 10.535 L 15.288 13.169 L 12.508 13.169 Z M 1.391 10.535 L 4.17 10.535 L 4.17 13.169 L 1.39 13.169 L 1.39 10.535 Z",
    ],
  },
  {
    viewBox: "0 0 12 12",
    width: 12,
    height: 12,
    paths: [
      "M 12 6 C 12 6.276 11.776 6.5 11.5 6.5 L 6.5 6.5 L 6.5 11.5 C 6.5 11.776 6.276 12 6 12 C 5.724 12 5.5 11.776 5.5 11.5 L 5.5 6.5 L 0.5 6.5 C 0.224 6.5 0 6.276 0 6 C 0 5.724 0.224 5.5 0.5 5.5 L 5.5 5.5 L 5.5 0.5 C 5.5 0.224 5.724 0 6 0 C 6.276 0 6.5 0.224 6.5 0.5 L 6.5 5.5 L 11.5 5.5 C 11.776 5.5 12 5.724 12 6 Z",
    ],
  },
];

export type CcProductCanvasTheme = "dark" | "light";
export type CcProductCanvasLayout = "full" | "embedded";

/** When set, the workflow band scrolls horizontally instead of shrinking or clipping. */
export type CcProductCanvasFlowOverflow = "horizontal-scroll";

export type CcProductCanvasNodeIconBrand = CcProductCanvasBrandIconId;

export type CcProductCanvasFlowNodeCopy = {
  title: string;
  subtitle?: string;
  /** Lucide icon name (see `auto-flow-icons.ts`). */
  icon?: AutoFlowIconName;
  /** HubSpot and other brand marks used elsewhere on service pages. */
  iconBrand?: CcProductCanvasNodeIconBrand;
};

export type CcProductCanvasFlowCopy = {
  workflowTitle: string;
  nodes: {
    email: CcProductCanvasFlowNodeCopy;
    fields: CcProductCanvasFlowNodeCopy;
    code: CcProductCanvasFlowNodeCopy;
    fields1: CcProductCanvasFlowNodeCopy;
    if: CcProductCanvasFlowNodeCopy;
    telegram: CcProductCanvasFlowNodeCopy;
    send: CcProductCanvasFlowNodeCopy;
  };
  agent: {
    primary: string;
    secondary: string;
    iconBrand?: CcProductCanvasNodeIconBrand;
  };
};

export type CcProductCanvasProps = {
  eyebrow?: string;
  title?: string;
  body?: string;
  /** Swaps in-stage node labels and workflow title (graph layout stays fixed). */
  flow?: CcProductCanvasFlowCopy;
  theme?: CcProductCanvasTheme;
  layout?: CcProductCanvasLayout;
  showTiles?: boolean;
  showHeading?: boolean;
  /** Left-rail STACK label + icon grid (decorative). */
  showStack?: boolean;
  /** Frame chrome: hatch ruler, left rail line, AUTO bracket, hash bar. */
  showFrameOrnaments?: boolean;
  /** In-stage toolbar: undo/redo, mode chip, workflow title row. */
  showChrome?: boolean;
  /** Renders at the top inside the rounded product frame (above stage chrome). */
  frameHeader?: ReactNode;
  /** Renders at the bottom inside the rounded product frame (below stage chrome). */
  frameFooter?: ReactNode;
  /** Hero embeds: pan the fixed-width graph inside the frame on narrow viewports. */
  flowOverflow?: CcProductCanvasFlowOverflow;
  id?: string;
  devSectionKey?: string;
  navTheme?: "dark" | "light";
};

const FLOW_CROSSFADE_MS = 220;

function flowContentSignature(flow: CcProductCanvasFlowCopy): string {
  const { nodes, agent } = flow;
  return [
    flow.workflowTitle,
    agent.primary,
    agent.secondary,
    agent.iconBrand ?? "",
    nodes.email.title,
    nodes.email.subtitle ?? "",
    nodes.email.icon ?? "",
    nodes.email.iconBrand ?? "",
    nodes.fields.title,
    nodes.code.title,
    nodes.fields1.title,
    nodes.if.title,
    nodes.telegram.title,
    nodes.send.title,
  ].join("\u0001");
}

function Port({ side }: { side: "in" | "out" | "out-top" | "out-bottom" }) {
  return (
    <span
      className={`cc-product-canvas__port cc-product-canvas__port--${side}`}
      aria-hidden="true"
    />
  );
}

function StackIcon({ index }: { index: number }) {
  const icon = STACK_ICONS[index];

  return (
    <svg
      className="cc-product-canvas__stack-icon"
      viewBox={icon.viewBox}
      width={icon.width}
      height={icon.height}
      preserveAspectRatio="none"
      fill="none"
      aria-hidden="true"
    >
      {icon.paths.map((d) => (
        <path key={d} d={d} fill="currentColor" />
      ))}
    </svg>
  );
}

function StageTop({ workflowTitle, agentMode }: { workflowTitle: string; agentMode: string }) {
  return (
    <div className="cc-product-canvas__stage-top">
      <div className="cc-product-canvas__top-group">
        <span className="cc-product-canvas__top-icon" aria-hidden="true">
          <svg
            viewBox="0 0 11.949 9.206"
            width="12"
            height="9"
            preserveAspectRatio="none"
            fill="none"
          >
            <path d={UNDO_PATH} fill="#fff" />
          </svg>
        </span>
        <span
          className="cc-product-canvas__top-icon cc-product-canvas__top-icon--redo"
          aria-hidden="true"
        >
          <svg
            viewBox="0 0 11.949 9.206"
            width="12"
            height="9"
            preserveAspectRatio="none"
            fill="none"
          >
            {/* Armory dims the glyph, not the box: opacity on the 36x36 span
                would fade its border away alongside undo's. */}
            <path d={REDO_PATH} fill="#fff" fillOpacity="0.43" />
          </svg>
        </span>
      </div>

      <div className="cc-product-canvas__top-group">
        <span className="cc-product-canvas__chip">
          <span className="cc-product-canvas__chip-label">{agentMode}</span>
          <svg
            className="cc-product-canvas__chip-icon"
            viewBox="0 0 12.078 11.078"
            width="12"
            height="11"
            preserveAspectRatio="none"
            overflow="visible"
            fill="none"
            aria-hidden="true"
          >
            <path
              d={SPARKLE_PATH}
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="0.8"
              strokeMiterlimit="10"
            />
          </svg>
        </span>
        <span className="cc-product-canvas__untitled">
          <span className="cc-product-canvas__chip-label">{workflowTitle}</span>
          <svg
            className="cc-product-canvas__untitled-icon"
            viewBox="0 0 10.601 14.303"
            width="11"
            height="15"
            preserveAspectRatio="none"
            fill="none"
            aria-hidden="true"
          >
            <path d={CANVAS_GLYPH_PATH} fill="#fff" />
          </svg>
        </span>
      </div>
    </div>
  );
}

function NodeLabel({
  title,
  subtitle,
  compact,
}: {
  title: string;
  subtitle?: string;
  compact?: boolean;
}) {
  return (
    <span
      className={`cc-product-canvas__node-label${compact ? " cc-product-canvas__node-label--compact" : ""}`}
    >
      <span className="cc-product-canvas__node-title">{title}</span>
      {subtitle ? (
        <span className="cc-product-canvas__node-sub">{subtitle}</span>
      ) : null}
    </span>
  );
}

function NodeMark({ name }: { name: NodeMarkName }) {
  const mark = NODE_MARKS[name];

  return (
    <svg
      className="cc-product-canvas__node-mark cc-product-canvas__node-mark--legacy"
      viewBox={mark.viewBox}
      width={mark.width}
      height={mark.height}
      preserveAspectRatio="none"
      fill="none"
      aria-hidden="true"
    >
      <path d={mark.d} fill="#fff" />
    </svg>
  );
}

function ProductCanvasBrandMark({
  brand,
  size,
  className,
}: {
  brand: CcProductCanvasBrandIconId;
  size: number;
  className: string;
}) {
  return (
    <span className={className} aria-hidden="true">
      <Image
        className="cc-product-canvas__brand-icon"
        src={getProductCanvasBrandLogoSrc(brand)}
        alt=""
        width={size}
        height={size}
        unoptimized
      />
    </span>
  );
}

function CanvasNodeIcon({
  node,
  fallbackMark,
}: {
  node: CcProductCanvasFlowNodeCopy;
  fallbackMark: NodeMarkName;
}) {
  if (node.iconBrand) {
    return (
      <ProductCanvasBrandMark
        brand={node.iconBrand}
        size={16}
        className="cc-product-canvas__node-mark cc-product-canvas__node-mark--brand"
      />
    );
  }

  if (node.icon) {
    const Icon = getAutoFlowIcon(node.icon);
    return (
      <Icon
        className="cc-product-canvas__node-mark cc-product-canvas__node-mark--lucide"
        size={16}
        strokeWidth={1.5}
        aria-hidden
      />
    );
  }

  return <NodeMark name={fallbackMark} />;
}

function AgentMark({ flow }: { flow: CcProductCanvasFlowCopy }) {
  if (flow.agent.iconBrand) {
    return (
      <ProductCanvasBrandMark
        brand={flow.agent.iconBrand}
        size={18}
        className="cc-product-canvas__agent-mark cc-product-canvas__agent-mark--brand"
      />
    );
  }

  return (
    <span className="cc-product-canvas__agent-mark" aria-hidden="true">
      <svg viewBox="0 0 19 24" width="19" height="24" preserveAspectRatio="none" fill="none">
        <path d={AGENT_BOLT_PATH} fill="#fff" />
      </svg>
    </span>
  );
}

function TileIcon({ index }: { index: number }) {
  return (
    <svg viewBox="0 0 55 55" width="55" height="55" fill="none" aria-hidden="true">
      {TILE_ICONS[index].map((part, partIndex) => (
        <path
          key={`${index}-${partIndex}`}
          d={part.d}
          fill={part.fill}
          stroke={part.stroke}
          strokeWidth={part.strokeWidth}
          strokeLinecap="butt"
          strokeLinejoin="miter"
          strokeMiterlimit="10"
          transform={part.transform}
        />
      ))}
    </svg>
  );
}

export function CcProductCanvas({
  eyebrow: eyebrowProp,
  title: titleProp,
  body: bodyProp,
  flow: flowProp,
  theme = "dark",
  layout = "full",
  showTiles = true,
  showHeading = true,
  showStack = true,
  showFrameOrnaments = true,
  showChrome = true,
  frameHeader,
  frameFooter,
  flowOverflow,
  id = "product-canvas",
  devSectionKey = "product-canvas",
  navTheme = "dark",
}: CcProductCanvasProps = {}) {
  const copy = getProductCanvasCopy(useCopyLang());
  const eyebrow = eyebrowProp ?? copy.eyebrow;
  const title = titleProp ?? copy.title;
  const body = bodyProp ?? copy.body;
  const flow: CcProductCanvasFlowCopy = flowProp ?? copy.flow;
  const sectionRef = useRef<HTMLElement>(null);
  const [appeared, setAppeared] = useState(false);
  const [appearSettled, setAppearSettled] = useState(false);
  const [presentedFlow, setPresentedFlow] = useState(flow);
  const [isFlowCrossfadeOut, setIsFlowCrossfadeOut] = useState(false);
  const flowSignatureRef = useRef(flowContentSignature(flow));
  const flowCrossfadeTimerRef = useRef(0);
  const skipFlowCrossfadeRef = useRef(true);

  const onFrameTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget || event.propertyName !== "opacity") {
        return;
      }

      setAppearSettled(true);
    },
    [],
  );

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) {
      return;
    }

    let settleTimer = 0;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion || typeof IntersectionObserver === "undefined") {
      setAppeared(true);
      setAppearSettled(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }

        setAppeared(true);
        settleTimer = window.setTimeout(() => {
          setAppearSettled(true);
        }, 880);
        observer.disconnect();
      },
      { threshold: APPEAR_THRESHOLD, rootMargin: APPEAR_ROOT_MARGIN },
    );

    observer.observe(section);

    return () => {
      window.clearTimeout(settleTimer);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const nextSignature = flowContentSignature(flow);

    if (skipFlowCrossfadeRef.current) {
      skipFlowCrossfadeRef.current = false;
      flowSignatureRef.current = nextSignature;
      setPresentedFlow(flow);
      return;
    }

    if (nextSignature === flowSignatureRef.current) {
      return;
    }

    flowSignatureRef.current = nextSignature;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      setIsFlowCrossfadeOut(false);
      setPresentedFlow(flow);
      return;
    }

    window.clearTimeout(flowCrossfadeTimerRef.current);
    setIsFlowCrossfadeOut(true);

    flowCrossfadeTimerRef.current = window.setTimeout(() => {
      setPresentedFlow(flow);
      setIsFlowCrossfadeOut(false);
    }, FLOW_CROSSFADE_MS);

    return () => {
      window.clearTimeout(flowCrossfadeTimerRef.current);
    };
  }, [flow]);

  const eyebrowTheme = theme === "light" ? "on-subtle" : "on-dark";

  const sectionClassName = [
    "cc-product-canvas",
    theme === "light" ? "cc-product-canvas--light" : "",
    layout === "embedded" ? "cc-product-canvas--embedded" : "",
    !showStack ? "cc-product-canvas--no-stack" : "",
    !showChrome ? "cc-product-canvas--no-stage-chrome" : "",
    !showFrameOrnaments ? "cc-product-canvas--no-frame-ornaments" : "",
    frameHeader ? "cc-product-canvas--has-frame-header" : "",
    frameFooter ? "cc-product-canvas--has-frame-footer" : "",
    flowOverflow === "horizontal-scroll" ? "cc-product-canvas--flow-scroll-x" : "",
    appeared ? "is-appeared" : "",
    appearSettled ? "is-appear-settled" : "",
    isFlowCrossfadeOut ? "is-flow-crossfade-out" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      ref={sectionRef}
      className={sectionClassName}
      id={id}
      data-cc-nav={navTheme}
      {...devSectionAttr(devSectionKey)}
    >
      <div className="cc-product-canvas__noise" aria-hidden="true" />

      <div className="cc-product-canvas__rules" aria-hidden="true">
        <span className="cc-product-canvas__rules-bay">
          <span className="cc-product-canvas__rule" />
        </span>
        <span className="cc-product-canvas__rule" />
        <span className="cc-product-canvas__rules-bay">
          <span className="cc-product-canvas__rule" />
        </span>
      </div>

      {showHeading ? (
        <header className="cc-product-canvas__heading">
          <div className="cc-product-canvas__heading-group">
            <div className="cc-product-canvas__eyebrow">
              <CcSanjayaEyebrow theme={eyebrowTheme}>{eyebrow}</CcSanjayaEyebrow>
            </div>
            <h2 className="cc-product-canvas__title">{title}</h2>
          </div>
          <p className="cc-product-canvas__body">{body}</p>
        </header>
      ) : null}

      <div className="cc-product-canvas__board">
        <div className="cc-product-canvas__scroller" data-hide-scrollbars="true">
          <div
            className="cc-product-canvas__frame"
            data-cc-stage="agent"
            data-stage="agent"
            onTransitionEnd={onFrameTransitionEnd}
          >
            {frameHeader ? (
              <div className="cc-product-canvas__frame-header">{frameHeader}</div>
            ) : null}
            <div className="cc-product-canvas__chrome">
              <div className="cc-product-canvas__rail">
                <div className="cc-product-canvas__tabs" aria-label={copy.chrome.modeLabel}>
                  <span
                    className="cc-product-canvas__tab is-active"
                    data-cc-tab="agent"
                  >{copy.chrome.agent}</span>
                </div>

                {showStack ? (
                  <div className="cc-product-canvas__stack">
                    <p className="cc-product-canvas__stack-label">{copy.chrome.stack}</p>
                    <div className="cc-product-canvas__stack-grid">
                      {STACK_ICONS.map((icon, index) => (
                        <span
                          key={icon.paths[0]}
                          className="cc-product-canvas__stack-tile"
                          aria-hidden="true"
                        >
                          <StackIcon index={index} />
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="cc-product-canvas__viewport">
                <div
                  className="cc-product-canvas__flow-scroll"
                  {...(flowOverflow === "horizontal-scroll"
                    ? { "data-hide-scrollbars": "true" as const }
                    : {})}
                >
                  <div
                    className="cc-product-canvas__stage cc-product-canvas__stage--agent"
                    data-cc-stage-panel="agent"
                    data-cc-active="true"
                  >
                    <div className="cc-product-canvas__flow-surface">
                    {showChrome ? (
                      <StageTop workflowTitle={presentedFlow.workflowTitle} agentMode={copy.chrome.agentMode} />
                    ) : null}

                    <div className="cc-product-canvas__graph">
                      <div className="cc-product-canvas__grid" aria-hidden="true" />

                      <div className="cc-product-canvas__nodes">
                      <svg
                        className="cc-product-canvas__connectors cc-product-canvas__connectors--branch"
                        viewBox="0 0 273 268"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          className="cc-product-canvas__path cc-product-canvas__path--branch"
                          d={BRANCH_PATH}
                          stroke="#fff"
                          strokeWidth="0.7"
                          strokeDasharray="13.33386962890625 13.33386962890625"
                          strokeLinecap="butt"
                          strokeLinejoin="miter"
                          strokeMiterlimit="10"
                          transform="translate(4.5 4.5)"
                        />
                      </svg>
                      <svg
                        className="cc-product-canvas__connectors cc-product-canvas__connectors--main"
                        viewBox="0 0 693 202"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          className="cc-product-canvas__path cc-product-canvas__path--main"
                          d={MAIN_PATH}
                          stroke="#fff"
                          strokeWidth="0.7"
                          strokeDasharray="17.595205078125 17.595205078125"
                          strokeLinecap="butt"
                          strokeLinejoin="miter"
                          strokeMiterlimit="10"
                          transform="translate(2 2.5)"
                        />
                      </svg>

                        <div className="cc-product-canvas__node cc-product-canvas__node--email">
                          <CanvasNodeIcon node={presentedFlow.nodes.email} fallbackMark="email" />
                          <Port side="out" />
                          <NodeLabel
                            title={presentedFlow.nodes.email.title}
                            subtitle={presentedFlow.nodes.email.subtitle}
                          />
                        </div>

                        <div className="cc-product-canvas__node cc-product-canvas__node--fields">
                          <CanvasNodeIcon node={presentedFlow.nodes.fields} fallbackMark="fields" />
                          <Port side="in" />
                          <Port side="out" />
                          <NodeLabel
                            title={presentedFlow.nodes.fields.title}
                            subtitle={presentedFlow.nodes.fields.subtitle}
                          />
                        </div>

                        <div className="cc-product-canvas__node cc-product-canvas__node--agent">
                          <AgentMark flow={presentedFlow} />
                          <span className="cc-product-canvas__agent-copy">
                            <span>{presentedFlow.agent.primary}</span>
                            <span>{presentedFlow.agent.secondary}</span>
                          </span>
                          <Port side="in" />
                          <Port side="out" />
                        </div>

                        <div className="cc-product-canvas__node cc-product-canvas__node--code">
                          <CanvasNodeIcon node={presentedFlow.nodes.code} fallbackMark="code" />
                          <Port side="in" />
                          <Port side="out" />
                          <NodeLabel
                            title={presentedFlow.nodes.code.title}
                            subtitle={presentedFlow.nodes.code.subtitle}
                          />
                        </div>

                        <div className="cc-product-canvas__node cc-product-canvas__node--fields1">
                          <CanvasNodeIcon node={presentedFlow.nodes.fields1} fallbackMark="fields" />
                          <Port side="in" />
                          <Port side="out" />
                          <NodeLabel
                            title={presentedFlow.nodes.fields1.title}
                            subtitle={presentedFlow.nodes.fields1.subtitle}
                          />
                        </div>

                        <div className="cc-product-canvas__node cc-product-canvas__node--if">
                          <CanvasNodeIcon node={presentedFlow.nodes.if} fallbackMark="if" />
                          <Port side="in" />
                          <Port side="out-top" />
                          <Port side="out-bottom" />
                          <NodeLabel
                            title={presentedFlow.nodes.if.title}
                            subtitle={presentedFlow.nodes.if.subtitle}
                          />
                        </div>

                        <div className="cc-product-canvas__node cc-product-canvas__node--telegram">
                          <CanvasNodeIcon
                            node={presentedFlow.nodes.telegram}
                            fallbackMark="telegram"
                          />
                          <Port side="in" />
                          <Port side="out" />
                          <NodeLabel
                            title={presentedFlow.nodes.telegram.title}
                            subtitle={presentedFlow.nodes.telegram.subtitle}
                          />
                        </div>

                        <div className="cc-product-canvas__node cc-product-canvas__node--send">
                          <CanvasNodeIcon node={presentedFlow.nodes.send} fallbackMark="send" />
                          <Port side="in" />
                          <Port side="out" />
                          <NodeLabel
                            title={presentedFlow.nodes.send.title}
                            subtitle={presentedFlow.nodes.send.subtitle}
                          />
                        </div>

                        <button
                          type="button"
                          className="cc-product-canvas__add"
                          aria-label={copy.chrome.addNode}
                        >
                          <svg
                            viewBox="0 0 8 8"
                            width="8"
                            height="8"
                            preserveAspectRatio="none"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path d={PLUS_PATH} fill="#fff" />
                          </svg>
                        </button>

                        <span className="cc-product-canvas__edge cc-product-canvas__edge--1">{copy.chrome.oneItem}</span>
                        <span className="cc-product-canvas__edge cc-product-canvas__edge--2">{copy.chrome.oneItem}</span>
                        <span className="cc-product-canvas__edge cc-product-canvas__edge--3">{copy.chrome.oneItem}</span>
                        <span className="cc-product-canvas__edge cc-product-canvas__edge--4">{copy.chrome.oneItem}</span>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              </div>
            </div>

            {frameFooter ? (
              <div className="cc-product-canvas__frame-footer">{frameFooter}</div>
            ) : null}

            <div className="cc-product-canvas__ruler" aria-hidden="true">
              <svg
                viewBox="0 0 13.995 94.983"
                width="14"
                height="95"
                preserveAspectRatio="none"
                fill="none"
              >
                <path d={HATCH_RULER_PATH} fill="#fff" />
              </svg>
            </div>

            <div
              className="cc-product-canvas__line cc-product-canvas__line--left"
              aria-hidden="true"
            >
              {/* The 1px-wide box carries a 10-unit stroke centred on x=0, so
                  the paint has to spill past the viewBox to fill the 3px rail —
                  Armory sets `overflow: visible` on this svg for the same reason. */}
              <svg
                viewBox="0 0 1 206"
                width="1"
                height="206"
                preserveAspectRatio="none"
                overflow="visible"
                fill="none"
              >
                <path
                  d="M 0 206 L 0 0"
                  stroke="#fff"
                  strokeWidth="10"
                  strokeMiterlimit="10"
                />
              </svg>
            </div>

            <div className="cc-product-canvas__host" aria-hidden="true">{copy.chrome.host}</div>

            <div className="cc-product-canvas__auto" aria-hidden="true">
              <svg
                viewBox="0 0 85.5 59"
                width="86"
                height="59"
                preserveAspectRatio="none"
                overflow="visible"
                fill="none"
              >
                <path
                  d={BRACKET_PATH}
                  stroke="#fff"
                  strokeWidth="3"
                  strokeMiterlimit="10"
                />
              </svg>
              <span className="cc-product-canvas__auto-label">{copy.chrome.auto}</span>
            </div>

            <div className="cc-product-canvas__hashes" aria-hidden="true">
              <svg
                viewBox="0 0 93.865 16.562"
                width="94"
                height="17"
                preserveAspectRatio="none"
                fill="none"
              >
                <path d={HASH_BAR_PATH} fill="#fff" />
              </svg>
            </div>

            <div
              className="cc-product-canvas__line cc-product-canvas__line--right"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 3 291"
                width="3"
                height="291"
                preserveAspectRatio="none"
                fill="none"
              >
                <path d={RIGHT_LINE_PATH} fill="#fff" />
              </svg>
            </div>

            <div className="cc-product-canvas__you">
              <svg
                className="cc-product-canvas__pointer"
                viewBox="0 0 17 20"
                width="17"
                height="20"
                preserveAspectRatio="none"
                fill="none"
                aria-hidden="true"
              >
                <path d={POINTER_PATH} fill="#fff" />
              </svg>
              <span className="cc-product-canvas__you-pill">{copy.chrome.you}</span>
            </div>

            <div className="cc-product-canvas__frame-noise" aria-hidden="true" />
          </div>
        </div>

        {showTiles ? (
          <div className="cc-product-canvas__tiles">
            {copy.tiles.map((tile, index) => (
              <article
                key={tile.title}
                className={`cc-product-canvas__tile cc-product-canvas__tile--${index + 1}`}
              >
                <div className="cc-product-canvas__tile-noise" aria-hidden="true" />
                <TileIcon index={index} />
                <div className="cc-product-canvas__tile-copy">
                  <h3 className="cc-product-canvas__tile-title">{tile.title}</h3>
                  <p className="cc-product-canvas__tile-body">{tile.body}</p>
                </div>
              </article>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
